import {DestroyRef, inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, combineLatestWith, merge, Observable, Subject} from 'rxjs';
import {map, skip, take, tap} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { MenuStoreService } from './menu-store.service';
import { MenuApiService } from '@emm/menu/services/menu-api.service';
import {TreeUtil} from '@emm/core/util/angular-tree-component.util';
import {genericSort} from '@emm/core/util/sort.util';
import {SubmenusStoreService} from '@emm/menu/store/submenus/submenus-store.service';
import {ItemSummary} from '@emm/core/models/item-summary.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class MenuTreeFacadeService {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    private refresh = new Subject<string>();
    refresh$ = this.refresh.asObservable();
    isFutureDate$ = this.menuStore.queryBase.isFutureDate$;
    effectiveDate$ = this.menuStore.queryBase.effectiveDate$;
    selectedFeatureScope$ = this.menuStore.queryFeatureScope.selected$;
    selectedConsumer$ = this.menuStore.queryConsumers.selected$;
    selectedLocationSet$ = this.menuStore.queryLocationSets.selected$;
    selectedLocationSetType$ = this.menuStore.queryLocationSetTypes.selected$;
    selectedVenue$ = this.menuStore.queryLocationSets.selectedVenue$;
    selectedSubmenu$ = this.menuStore.querySubmenus.selected$;
    selectedSearchSubmenu$ = this.menuStore.querySubmenus.selectedSearchSubmenu$;
    isEmpty$ = this.menuStore.queryItems.isEmpty$;
    menuKeyContext$ = this.menuStore.queryConsumers.menuParamsContext$.pipe(map(menuKey => menuKey));
    items$ = this.menuStore.queryItems.all$;
    isLoading$ = this.menuStore.isLoading$.submenus.pipe(
        combineLatestWith(
            [this.menuStore.isLoading$.itemChildren,
            this.menuStore.isLoading$.items,
            ]),
        map((sources: [boolean, Observable<boolean>]) => R.any(R.equals(true), sources))
    );
    isLoadingSubmenuItems$ = this.menuStore.isLoading$.items;
    itemsAsNodes$ = combineLatest([this.selectedFeatureScope$, this.items$]).pipe(
        map(([featureScope, items]) => TreeUtil.mapItemsToNode(items, featureScope))
    );
    updateMenuItem$ = this.menuStore.updateMenuItem$;
    selectedItemDetail$ = this.menuStore.queryItemDetail.selected$;
    anyLoading$ = this.menuStore.anyLoading$;

    submenus$ = this.menuStore.querySubmenus.all$;
    submenusAsGenericItems$ = this.submenus$
        .pipe(map(submenus => submenus.sort(genericSort(a => a.name))));
    submenusSortedByTag$ = this.submenus$
        .pipe(map(submenus => submenus.sort(genericSort(a => a.id))));
    selectedSubmenusPaste$ = this.menuStore.queryItemDetail.selectedSubmenusPasteCascade$;
    selectedSubmenusDelete$ = this.menuStore.queryItemDetail.selectedSubmenusDeleteCascade$;
    selectedSubmenusUpdate$ = this.menuStore.queryItemDetail.selectedSubmenusUpdateCascade$;
    isCopied = this.menuStore.queryItemDetail.isCopied;
    canPublish$ = new BehaviorSubject<boolean>(false);

    constructor(private menuStore: MenuStoreService,
                private submenuStore: SubmenusStoreService,
                private api: MenuApiService,) {}



    getChildNodes(path: string): Promise<any> {
        this.menuStore.dispatch.loadItemChildren(path);
        const updatedChildItems$ = this.menuStore.selectItemChildren(path).pipe(
            skip(1),
            take(1),
            map((children: unknown) => {
                const childrenArray = children as any[];
                if (!childrenArray || !Array.isArray(childrenArray)) {
                    return [];
                }
                return childrenArray;
            })
        );

        let scope: string;
        this.selectedFeatureScope$.pipe(take(1)).subscribe(value => scope = value);

        const childNodes$ = updatedChildItems$.pipe(
            take(1),
            map((children: any) => TreeUtil.mapItemsToNode(children, scope))
        );

        return childNodes$.toPromise();
    }


    selectItem(item: ItemSummary, dispatchReload: boolean = true): void {
        this.menuStore.dispatch.selectItem(item, dispatchReload);
    }

    selectSearchItem(item: ItemSummary, dispatchReload: boolean = true): void {
        const correctedItemSummary: ItemSummary = {
            childCount: item.childCount,
            hasChildren: item.hasChildren,
            disabled: item.disabled,
            disabledSource: item.disabledSource,
            disabledCode: item.disabledCode,
            id: item.id,
            jsonPath: item.jsonPath,
            isSellable: item.isSellable,
            name: item.name,
            path: item.path,
            tag: item.tag,
            typeName: item.typeName,
            tagPath: item.id,
        }
        let currentSearchSubmenu;
        this.selectedSearchSubmenu$.pipe(take(1)).subscribe(value => {currentSearchSubmenu = value});
        const submenu = item.id.split('/')[1];
        if (currentSearchSubmenu === submenu) {
            this.menuStore.dispatch.selectItem(correctedItemSummary, dispatchReload);
        } else {
            this.menuStore.dispatch.selectSearchSubmenu(submenu);
            this.menuStore.dispatch.selectCascadeSubmenus(submenu);
            this.menuStore.dispatch.selectItem(correctedItemSummary, dispatchReload);
        }
    }


    reloadItems(jsonPath: string = null): any {
        const parentPath = TreeUtil.getParentPath(jsonPath);
        if (RA.isNonEmptyString(parentPath)) {
            this.refresh.next(jsonPath);
        } else {
            this.menuStore.dispatch.loadItems();
        }
        return merge(this.menuStore.loadItems$, this.menuStore.loadItemChildren$).pipe(take(1));
    }



    addItem(parent: ItemDetail, children: ItemDetail[]): any {
        this.menuStore.dispatch.addItem(parent, children);
        return this.menuStore.addItem$.pipe(take(1));
    }


    addSubmenu(submenu: ItemDetail): any {
        this.menuStore.dispatch.addSubmenu(submenu);
        return this.menuStore.addSubmenu$.pipe(take(1));
    }


    loadSubmenus() {
        return this.submenuStore.dispatch.loadSubmenus();
    }



    filterSubmenus(submenus) {
        this.submenuStore.dispatch.filterSubmenus(submenus);
    }



    toggleItemDisabled(path: string, updates: { disabled: boolean }, forceOverrideInheritance: boolean): any {
        this.menuStore.dispatch.toggleItemDisabled(path, updates, false, forceOverrideInheritance);
        return this.menuStore.toggleItemDisabled$.pipe(take(1));
    }



    deleteItem(path: string, itemName: string, selectedItem?: ItemDetail): any {
        if ((selectedItem && selectedItem.tagPath === path) || !selectedItem) {
            this.menuStore.dispatch.deleteItemWithoutReload(path, itemName);
        } else if (selectedItem) {
            this.menuStore.dispatch.deleteItem(path, itemName, selectedItem);
        } else {
            this.menuStore.dispatch.deleteItemWithoutReload(path, itemName);
        }
        return this.menuStore.deleteItem$.pipe(take(1));
    }

    pasteItemCascade(submenuTag: string, parentTag: string, childTag: string, selectedItem: ItemDetail, position: string): any {
        this.menuStore.dispatch.pasteItemCascade(submenuTag, parentTag, childTag, selectedItem, position);
        return this.menuStore.pasteCascade$.pipe(take(1));
    }



    deleteItemCascade(path: string, selectedItem: ItemDetail): any {
        this.menuStore.dispatch.deleteItemCascade(path, selectedItem);
        return this.menuStore.deleteCascade$.pipe(take(1));
    }



    copyItem(fromPath: string): any {
        this.menuStore.dispatch.copyItem(fromPath);
        return this.menuStore.copyItem$.pipe(take(1));
    }



    pasteItem(fromPath: string, toPath: string): any {
        this.menuStore.dispatch.pasteItem(fromPath, toPath);
        return this.menuStore.pasteItem$.pipe(take(1));
    }


    moveItem(fromPath: string, toPath: string, index?: string): any {
        this.menuStore.dispatch.moveItem(fromPath, toPath, index);
        return this.menuStore.moveItem$.pipe(take(1));
    }



    redirectToDate(date: string): void {
        this.menuStore.dispatch.redirectToDate(date);
    }



    loadFutureChangeSets(): void {
        this.menuStore.dispatch.loadFutureChangeSets();
    }



    selectSubmenu(submenu: string): void {
        this.menuStore.dispatch.selectSubmenu(submenu);
    }



    deselectSubmenu(submenu: string): void {
        this.menuStore.dispatch.deselectSubmenu(submenu);
    }



    viewChangeSets(): void {
        this.menuStore.dispatch.openChangeSetsDialog();
    }



    saveAssignments(assignments: any[]): void {
        this.menuStore.dispatch.saveAssignments(assignments);
    }

    selectCascadeSubmenus(selectedSubmenuTag: string) {
        this.menuStore.dispatch.selectCascadeSubmenus(selectedSubmenuTag);
    }

    deselectCascadeSubmenus(selectedSubmenuTag: string) {
        this.menuStore.dispatch.deselectCascadeSubmenus(selectedSubmenuTag);
    }

    resetCascadeSubmenus() {
        this.menuStore.dispatch.resetCascadeSubmenus();
    }

    changeConsumerContext() {
        this.menuStore.dispatch.getAllConsumers();
    }

    renameSubmenu(submenuId: string, newName: string, effectiveDate?: string) {
        this.api.renameSubmenu(submenuId, newName, effectiveDate)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
}
